<template>
    <div class="@container/item">
        <div class="border border-gray-200 rounded-2xl flex @md/item:flex-row flex-col @md/item:gap-6 overflow-hidden relative hover:shadow-custom-xl h-full focus" :class="{ 'bg-pink-50 border-pink-100': item.isFeatured }" tabindex="0">
            <!-- Image -->
            <div class="w-full @md/item:w-64 relative min-h-54 shrink-0">
                <img :src="item.cover?.url || '/assets/images/placeholder/product-placeholder.png'" :alt="item.name + ' - ' + item.fullLocation" class="w-full h-full object-cover" />
                <template v-if="item.promoLabel">
                    <Tag :value="item.promoLabel" severity="secondary" class="absolute top-3 left-3" />
                </template>
            </div>

            <!-- Content -->

            <div class="py-3 @md/item:py-4 space-y-2 w-full @md/item:flex-1 pr-3 @md/item:pr-4 pl-3 @md/item:pl-0 flex flex-col flex-1">
                <div class="space-y-1">
                    <ProductCategory :value="item.property?.category" />
                    <h2 class="text-base @sm/item:text-lg font-normal text-gray-700 line-clamp-1">
                        {{ item.name }}
                    </h2>
                </div>

                <div class="flex justify-between flex-1 @xl/item:flex-row flex-col gap-2">
                    <div class="space-y-2">
                        <!-- Properties -->

                        <div class="flex flex-wrap text-sm text-gray-700 [&>span:not(:first-child)]:before:content-['•'] [&>span:not(:first-child)]:before:mx-1 [&>span:not(:first-child)]:before:text-gray-400">
                            <template v-if="item.maxOccupancy">
                                <span class="font-semibold">{{ $t('max_occupancy', item.maxOccupancy) }}</span>
                            </template>

                            <ProductItemProperties :properties="item.properties" />
                        </div>

                        <!-- Rating -->
                        <ProductAvgReview :avgScore="item.avgScore" variant="small" v-if="item.avgScore" />

                        <!-- Location -->
                        <ProductLocation :fullLocation="item.fullLocation" />
                    </div>

                    <!-- Call To Action -->
                    <div class="@md/item:text-right flex @md/item:flex-col flex-row justify-between @md/item:items-end items-center @md/item:justify-end gap-4 mt-auto">
                        <template v-if="item.price">
                            <div class="space-y-0.5">
                                <div class="text-[13px] text-gray-700 space-x-1">
                                    <span>{{ $t('from') }}</span>
                                    <span v-if="item.price.discount" class="text-sm text-primary line-through">
                                        {{ $n(Number(item.price.original), 'currency') }}
                                    </span>
                                    <span class="text-lg text-gray-700 font-semibold">
                                        {{ $n(Number(item.price.final), 'currency') }}
                                    </span>
                                </div>
                                <template v-if="item.price.alternative == true">
                                    <div class="text-[13px] text-pink-500 font-medium">
                                        {{ $t('Stay:') }}
                                        <span>{{ $d(item.price.startDate, 'short') }} - {{ $t('nights', item.price.nights) }}</span>
                                    </div>
                                </template>
                                <div class="text-[13px] text-gray-700">
                                    {{ $n(Number(item.price.perNight), 'currency') }}
                                    <span>{{ $t('per night') }}</span>
                                </div>
                            </div>
                        </template>
                        <template v-else-if="!hideUnavailableMessage">
                            <Message severity="error" variant="simple" role="alert">
                                {{ hasStartDate ? $t('Not available for there dates. Select different dates.') : $t('Select date') }}
                            </Message>
                        </template>
                        <div>
                            <a :href="productHref" class="btn btn-primary btn-md ml-auto">
                                <span class="absolute inset-0"></span>
                                {{ $t('View Product') }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import ProductItemProperties from './ProductItemProperty.vue'
import ProductCategory from './ProductCategory.vue'
import { pickProductContextQuery } from '@/composables/searchQuery'

const props = defineProps({
    item: {
        type: Object,
        required: true
    },
    productContextQuery: {
        type: Object,
        required: false
    },
    hideUnavailableMessage: {
        type: Boolean,
        default: false
    }
})

const router = useRouter()
const route = useRoute()
const routeProductContextQuery = computed(() => pickProductContextQuery(route.query))

const productContextQuery = computed(() => {
    return {
        start_date: props.item?.price?.startDate,
        nights: props.item?.price?.nights,
        adults: routeProductContextQuery.value?.adults,
        children: routeProductContextQuery.value?.children
    }
})
const hasStartDate = computed(() => Boolean(productContextQuery.value?.start_date))
const nights = computed(() => productContextQuery.value?.nights)

const productHref = computed(() => {
    if (!props.item?.url) return '#'

    return router.resolve({ path: '/ponudba/' + props.item.url, query: productContextQuery.value }).href
})
</script>
